import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Store, LogOut, User, Image, Video, Star, Instagram, Facebook,
  Globe, Phone, Mail, MapPin, Loader2, Check, X, Edit3, Plus, Trash2,
  Inbox, Send, MessageCircle, Calendar, Eye, Megaphone, BadgeCheck, Copy, Download, Settings, Lock
} from 'lucide-react';
import axios from 'axios';
import { vendorCategoryNames } from '@/lib/vendorCategories';
import {
  fetchVendorSelectableCategories,
  mergeWithSelectedCategories,
} from '@/lib/vendorCategorySource';
import PWAInstallGuide from '@/components/PWAInstallGuide';
import { downloadVendorInvoicePdf } from '@/lib/vendorInvoicePdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPaymentDate = (dateValue) => {
  if (!dateValue) return 'Not set';
  const parsedDate = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) return 'Not set';
  return parsedDate.toLocaleDateString('en-GB');
};

export default function VendorDashboard() {
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('messages');
  const [vendorUnreadCount, setVendorUnreadCount] = useState(0);
  const [adminUpdatesUnreadCount, setAdminUpdatesUnreadCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const forcePasswordChangeRequired = Boolean(vendor?.force_password_change || location.state?.forcePasswordChange);
  
  useEffect(() => {
    fetchVendorProfile();
    fetchSidebarCounts();
  }, []);

  useEffect(() => {
    if (forcePasswordChangeRequired && activeSection !== 'account') {
      setActiveSection('account');
    }
  }, [forcePasswordChangeRequired, activeSection]);
  
  const fetchVendorProfile = async () => {
    const token = localStorage.getItem('vendorToken');
    if (!token) {
      navigate('/vendor/auth');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/vendor/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendor(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('vendorToken');
        localStorage.removeItem('vendorData');
        localStorage.removeItem('vendorLastActivity');
        window.dispatchEvent(new Event('vendor-session-changed'));
        navigate('/vendor/auth');
      } else {
        setError(err.response?.data?.detail || 'Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('vendorToken');
    localStorage.removeItem('vendorData');
    localStorage.removeItem('vendorLastActivity');
    window.dispatchEvent(new Event('vendor-session-changed'));
    navigate('/vendor/auth');
  };

  const fetchSidebarCounts = async () => {
    const token = localStorage.getItem('vendorToken');
    if (!token) return;
    try {
      const [messageCountRes, adminUpdatesRes] = await Promise.all([
        axios.get(`${API}/vendor/messages/unread/count`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vendor/admin-messages`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setVendorUnreadCount(messageCountRes.data.unread_count || 0);
      setAdminUpdatesUnreadCount((adminUpdatesRes.data || []).filter((thread) => !thread.read || thread.status === 'unread').length);
    } catch (err) {
      console.error('Failed to load sidebar counts', err);
    }
  };

  const handleSectionChange = (section) => {
    if (forcePasswordChangeRequired && section !== 'account') {
      setActiveSection('account');
      return;
    }
    setActiveSection(section);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Button onClick={() => navigate('/vendor/auth')}>Back to Login</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-stone-50" data-testid="vendor-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="container-wedding py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_17b3eba4-e170-4c07-a419-15dd5729217a/artifacts/ndr9m4c4_logo%20blk%2026%20cropped.png" 
                alt="Gibraltar Wedding Expo" 
                className="h-16 md:h-20"
              />
            </Link>
            <div className="h-8 w-px bg-stone-200" />
            <div>
              <h1 className="font-semibold text-lg">{vendor?.business_name}</h1>
              <p className="text-xs text-stone-500">Vendor Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {vendor?.is_featured && (
              <span className="bg-[#D4AF37]/10 text-[#D4AF37] px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                <Star className="h-3 w-3 fill-current" /> Featured
              </span>
            )}
            <Button variant="ghost" onClick={handleLogout} className="text-stone-500">
              <LogOut className="h-4 w-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="container-wedding py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <nav className="bg-white rounded-sm border border-stone-200 overflow-hidden">
              <SidebarItem
                icon={Inbox}
                label="Messages"
                active={activeSection === 'messages'}
                onClick={() => handleSectionChange('messages')}
                disabled={forcePasswordChangeRequired}
                badge={vendorUnreadCount > 0 ? vendorUnreadCount : null}
              />
              <SidebarItem
                icon={User}
                label="Profile Info"
                active={activeSection === 'profile'}
                onClick={() => handleSectionChange('profile')}
                disabled={forcePasswordChangeRequired}
              />
              <SidebarItem
                icon={Image}
                label="Images & Logo"
                active={activeSection === 'media'}
                onClick={() => handleSectionChange('media')}
                disabled={forcePasswordChangeRequired}
              />
              <SidebarItem
                icon={Video}
                label="Video"
                active={activeSection === 'video'}
                onClick={() => handleSectionChange('video')}
                disabled={forcePasswordChangeRequired}
              />
              <SidebarItem
                icon={Star}
                label="Featured Status"
                active={activeSection === 'featured'}
                onClick={() => handleSectionChange('featured')}
                disabled={forcePasswordChangeRequired}
                badge={vendor?.featured_requested && !vendor?.is_featured ? 'Pending' : null}
              />
              <SidebarItem
                icon={Megaphone}
                label="Marketing Tools"
                active={activeSection === 'marketing'}
                onClick={() => handleSectionChange('marketing')}
                disabled={forcePasswordChangeRequired}
              />
              <SidebarItem
                icon={Mail}
                label="Admin Updates"
                active={activeSection === 'admin-updates'}
                onClick={() => handleSectionChange('admin-updates')}
                disabled={forcePasswordChangeRequired}
                badge={adminUpdatesUnreadCount > 0 ? adminUpdatesUnreadCount : null}
              />
              <SidebarItem
                icon={Download}
                label="Invoices"
                active={activeSection === 'invoices'}
                onClick={() => handleSectionChange('invoices')}
                disabled={forcePasswordChangeRequired}
              />
              <SidebarItem
                icon={Settings}
                label="Account"
                active={activeSection === 'account'}
                onClick={() => handleSectionChange('account')}
              />
            </nav>
            
            {/* Quick Stats */}
            <div className="bg-white rounded-sm border border-stone-200 p-4 mt-4">
              <h3 className="font-medium text-sm mb-3">Your Listing</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-stone-500">Status</span>
                  <span className={`font-medium ${vendor?.status === 'approved' ? 'text-green-600' : 'text-yellow-600'}`}>
                    {vendor?.status === 'approved' ? 'Active' : vendor?.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Categories</span>
                  <span className="font-medium">{vendor?.categories?.length || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Rating</span>
                  <span className="font-medium flex items-center gap-1">
                    <Star className="h-3 w-3 text-[#D4AF37] fill-current" />
                    {vendor?.average_rating?.toFixed(1) || '0.0'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-stone-500">Reviews</span>
                  <span className="font-medium">{vendor?.review_count || 0}</span>
                </div>
              <div className="flex justify-between">
                <span className="text-stone-500">Package</span>
                <span className="font-medium" data-testid="vendor-dashboard-sidebar-pricing-plan">{vendor?.pricing_plan || 'Not set'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone-500">Next Payment</span>
                <span className="font-medium" data-testid="vendor-dashboard-sidebar-next-payment-date">{formatPaymentDate(vendor?.next_payment_date)}</span>
              </div>
              </div>
            </div>
          </aside>
          
          {/* Main Panel */}
          <div className="lg:col-span-3">
            {forcePasswordChangeRequired && (
              <div className="mb-4 rounded-sm border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="vendor-force-password-change-alert">
                Your password was reset by admin. Please change it now before using other dashboard sections.
              </div>
            )}
            {activeSection === 'messages' && (
              <VendorMessagesSection onUnreadChange={setVendorUnreadCount} />
            )}
            {activeSection === 'profile' && (
              <ProfileSection vendor={vendor} onUpdate={fetchVendorProfile} />
            )}
            {activeSection === 'media' && (
              <MediaSection vendor={vendor} onUpdate={fetchVendorProfile} />
            )}
            {activeSection === 'video' && (
              <VideoSection vendor={vendor} onUpdate={fetchVendorProfile} />
            )}
            {activeSection === 'featured' && (
              <FeaturedSection vendor={vendor} onUpdate={fetchVendorProfile} />
            )}
            {activeSection === 'marketing' && (
              <MarketingSection vendor={vendor} onUpdate={fetchVendorProfile} />
            )}
            {activeSection === 'admin-updates' && (
              <VendorAdminUpdatesSection onUnreadChange={setAdminUpdatesUnreadCount} />
            )}
            {activeSection === 'invoices' && (
              <VendorInvoicesSection />
            )}
            {activeSection === 'account' && (
              <VendorAccountSection
                forcePasswordChangeRequired={forcePasswordChangeRequired}
                onPasswordChanged={async () => {
                  await fetchVendorProfile();
                }}
              />
            )}
          </div>
        </div>
      </main>
      
      {/* PWA Install Guide */}
      <PWAInstallGuide />
    </div>
  );
}

function SidebarItem({ icon: Icon, label, active, onClick, badge, disabled = false }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center justify-between px-4 py-3 border-b border-stone-100 last:border-0 transition-colors ${
        active
          ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
          : disabled
            ? 'text-stone-400 bg-stone-50 cursor-not-allowed'
            : 'text-stone-600 hover:bg-stone-50'
      }`}
    >
      <span className="flex items-center gap-3">
        <Icon className="h-4 w-4" />
        <span className="text-sm font-medium">{label}</span>
      </span>
      {badge && (
        <span className="bg-yellow-100 text-yellow-700 text-xs px-2 py-0.5 rounded-full">{badge}</span>
      )}
    </button>
  );
}

function ProfileSection({ vendor, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [autoSaveState, setAutoSaveState] = useState('idle');
  const [seoSuggestion, setSeoSuggestion] = useState('');
  const [seoSuggestionWordCount, setSeoSuggestionWordCount] = useState(0);
  const [seoSuggestLoading, setSeoSuggestLoading] = useState(false);
  const [seoSuggestError, setSeoSuggestError] = useState('');
  const [autoSuggestEnabled, setAutoSuggestEnabled] = useState(true);
  const [availableCategories, setAvailableCategories] = useState(vendorCategoryNames);
  const isHydratingFormRef = useRef(false);
  
  const selectedCategories = [
    formData.primaryCategory,
    ...(formData.secondaryCategories || []),
  ].filter(Boolean);

  const categoryOptions = mergeWithSelectedCategories(availableCategories, selectedCategories);

  useEffect(() => {
    let isMounted = true;

    const loadCategories = async () => {
      const categories = await fetchVendorSelectableCategories();
      if (isMounted) {
        setAvailableCategories(categories);
      }
    };

    loadCategories();

    return () => {
      isMounted = false;
    };
  }, []);
  
  useEffect(() => {
    if (vendor) {
      isHydratingFormRef.current = true;
      const primaryCategory = vendor.primaryCategory || vendor.category || (vendor.categories || [])[0] || '';
      const secondaryCategories = (vendor.secondaryCategories || (vendor.categories || []).filter((cat) => cat !== primaryCategory)).filter(Boolean);
      setFormData({
        business_name: vendor.business_name || '',
        description: vendor.description || '',
        address: vendor.address || '',
        phone: vendor.phone || '',
        primaryCategory,
        secondaryCategories,
        categories: primaryCategory ? [primaryCategory, ...secondaryCategories] : secondaryCategories,
        website: vendor.website || '',
        instagram: vendor.instagram || '',
        facebook: vendor.facebook || '',
        whatsapp: vendor.whatsapp || '',
        payment_method: vendor.payment_method || 'Bank Transfer',
        price_range: vendor.price_range || '$$'
      });
      setTimeout(() => {
        isHydratingFormRef.current = false;
      }, 0);
    }
  }, [vendor]);

  useEffect(() => {
    if (!editing || !autoSuggestEnabled) return undefined;
    const description = (formData.description || '').trim();
    if (description.length < 60) {
      setSeoSuggestion('');
      setSeoSuggestionWordCount(0);
      setSeoSuggestError('');
      return undefined;
    }

    const timer = window.setTimeout(async () => {
      setSeoSuggestLoading(true);
      setSeoSuggestError('');
      try {
        const token = localStorage.getItem('vendorToken');
        const response = await axios.post(
          `${API}/vendor/profile/description-suggestion`,
          { description },
          { headers: { Authorization: `Bearer ${token}` } },
        );

        setSeoSuggestion(response.data?.suggestion || '');
        setSeoSuggestionWordCount(response.data?.word_count || 0);
      } catch (error) {
        setSeoSuggestion('');
        setSeoSuggestionWordCount(0);
        setSeoSuggestError(error.response?.data?.detail || 'Could not generate SEO suggestion right now');
      } finally {
        setSeoSuggestLoading(false);
      }
    }, 1200);

    return () => window.clearTimeout(timer);
  }, [autoSuggestEnabled, editing, formData.description]);

  const syncCategorySelection = (primaryCategory, secondaryCategories) => {
    const normalizedSecondary = (secondaryCategories || []).filter((category) => category && category !== primaryCategory);
    setFormData((prev) => ({
      ...prev,
      primaryCategory,
      secondaryCategories: normalizedSecondary,
      categories: primaryCategory ? [primaryCategory, ...normalizedSecondary] : normalizedSecondary,
    }));
  };

  const handlePrimaryCategoryChange = (category) => {
    syncCategorySelection(category, formData.secondaryCategories || []);
  };

  const toggleSecondaryCategory = (category) => {
    if (category === formData.primaryCategory) return;

    if ((formData.secondaryCategories || []).includes(category)) {
      syncCategorySelection(
        formData.primaryCategory,
        (formData.secondaryCategories || []).filter((selected) => selected !== category),
      );
      return;
    }

    syncCategorySelection(
      formData.primaryCategory,
      [...(formData.secondaryCategories || []), category],
    );
  };
  
  const handleSave = async (silentAutoSave = false) => {
    if (!formData.primaryCategory) {
      if (!silentAutoSave) {
        alert('Primary category is required.');
      }
      return;
    }

    setLoading(true);
    setAutoSaveState('saving');
    try {
      const token = localStorage.getItem('vendorToken');
      const payload = {
        ...formData,
        primary_category: formData.primaryCategory,
        secondary_categories: formData.secondaryCategories || [],
        categories: formData.primaryCategory
          ? [formData.primaryCategory, ...(formData.secondaryCategories || [])]
          : [],
      };
      delete payload.primaryCategory;
      delete payload.secondaryCategories;

      await axios.put(`${API}/vendor/profile`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeoSuggestion('');
      setSeoSuggestionWordCount(0);
      setSeoSuggestError('');
      setAutoSaveState('saved');
      if (!silentAutoSave) {
        setEditing(false);
        onUpdate();
      }
    } catch (err) {
      setAutoSaveState('error');
      if (!silentAutoSave) {
        alert(err.response?.data?.detail || 'Failed to update profile');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!editing) return;
    if (isHydratingFormRef.current) return;
    if (!formData.primaryCategory) return;

    const timer = window.setTimeout(() => {
      handleSave(true);
    }, 150);

    return () => window.clearTimeout(timer);
  }, [editing, formData]);
  
  return (
    <div className="bg-white rounded-sm border border-stone-200">
      <div className="p-6 border-b border-stone-100 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Profile Information</h2>
          <p className="text-sm text-stone-500">Manage your business details</p>
        </div>
        {!editing ? (
          <Button onClick={() => setEditing(true)} variant="outline" size="sm">
            <Edit3 className="h-4 w-4 mr-2" /> Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <div className="text-xs text-stone-500 flex items-center" data-testid="vendor-profile-autosave-status">
              {autoSaveState === 'saving' ? 'Saving…' : autoSaveState === 'saved' ? 'Saved' : autoSaveState === 'error' ? 'Save failed' : 'Autosave enabled'}
            </div>
            <Button onClick={() => { setEditing(false); onUpdate(); }} variant="outline" size="sm">
              <Check className="h-4 w-4 mr-1" /> Done
            </Button>
          </div>
        )}
      </div>
      
      <div className="p-6 space-y-6">
        {/* Business Name */}
        <div>
          <Label className="text-stone-500 text-xs uppercase tracking-wide">Business Name</Label>
          {editing ? (
            <Input
              value={formData.business_name}
              onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
              className="mt-1"
            />
          ) : (
            <p className="font-medium mt-1">{vendor?.business_name}</p>
          )}
        </div>
        
        {/* Categories */}
        <div>
          <Label className="text-stone-500 text-xs uppercase tracking-wide">Categories</Label>
          {editing ? (
            <div className="space-y-3 mt-2" data-testid="vendor-profile-category-edit-section">
              <div>
                <Label className="text-[11px] tracking-wide text-stone-500">Primary Category *</Label>
                <Select value={formData.primaryCategory || undefined} onValueChange={handlePrimaryCategoryChange}>
                  <SelectTrigger className="mt-1" data-testid="vendor-profile-primary-category-trigger">
                    <SelectValue placeholder="Select primary category" />
                  </SelectTrigger>
                  <SelectContent data-testid="vendor-profile-primary-category-content">
                    {categoryOptions.map((category) => (
                      <SelectItem
                        key={category}
                        value={category}
                        data-testid={`vendor-profile-primary-category-option-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-[11px] tracking-wide text-stone-500">Secondary Categories (optional)</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                  {categoryOptions.map((category) => {
                    const isPrimary = formData.primaryCategory === category;
                    const isSelected = (formData.secondaryCategories || []).includes(category);

                    return (
                      <button
                        key={category}
                        type="button"
                        onClick={() => toggleSecondaryCategory(category)}
                        disabled={isPrimary}
                        className={`text-xs py-2 px-2 rounded-sm border transition-colors ${
                          isPrimary
                            ? 'bg-stone-100 border-stone-200 text-stone-400 cursor-not-allowed'
                            : isSelected
                              ? 'bg-[#D4AF37] border-[#D4AF37] text-white'
                              : 'bg-white border-stone-200 text-stone-600 hover:border-[#D4AF37]'
                        }`}
                        data-testid={`vendor-profile-secondary-category-option-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        {category}
                        {isPrimary ? ' (Primary)' : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 mt-2" data-testid="vendor-profile-category-readonly-section">
              {(vendor?.primaryCategory || vendor?.category) && (
                <span className="bg-[#5C0120]/10 text-[#5C0120] px-3 py-1 rounded-full text-sm" data-testid="vendor-profile-primary-category-badge">
                  {(vendor?.primaryCategory || vendor?.category)} (Primary)
                </span>
              )}
              {(vendor?.secondaryCategories || []).map((cat) => (
                <span key={cat} className="bg-[#D4AF37]/10 text-[#D4AF37] px-3 py-1 rounded-full text-sm" data-testid={`vendor-profile-secondary-category-badge-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  {cat}
                </span>
              ))}
            </div>
          )}
        </div>
        
        {/* Description */}
        <div>
          <Label className="text-stone-500 text-xs uppercase tracking-wide">Description</Label>
          {editing ? (
            <div className="space-y-3 mt-1" data-testid="vendor-description-editor-with-seo">
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full min-h-[120px] p-3 border border-stone-200 rounded-sm text-sm focus:outline-none focus:border-[#D4AF37]"
                data-testid="vendor-description-input"
              />

              <div className="rounded-sm border border-[#5C0120]/15 bg-[#fff8f2] p-3" data-testid="vendor-seo-suggestion-panel">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div>
                    <p className="text-sm font-semibold text-[#5C0120]" data-testid="vendor-seo-suggestion-title">SEO Description Suggestion (250+ words)</p>
                    <p className="text-xs text-stone-600" data-testid="vendor-seo-suggestion-subtitle">Auto-suggest improves your description for search visibility in Gibraltar.</p>
                  </div>
                  <label className="inline-flex items-center gap-2 text-xs text-stone-600" data-testid="vendor-seo-autosuggest-toggle-wrap">
                    <input
                      type="checkbox"
                      checked={autoSuggestEnabled}
                      onChange={(e) => setAutoSuggestEnabled(e.target.checked)}
                      data-testid="vendor-seo-autosuggest-toggle"
                    />
                    Auto suggest
                  </label>
                </div>

                {seoSuggestLoading ? (
                  <div className="flex items-center text-xs text-stone-600" data-testid="vendor-seo-suggestion-loading">
                    <Loader2 className="h-3 w-3 animate-spin mr-1" /> Generating SEO version…
                  </div>
                ) : seoSuggestError ? (
                  <p className="text-xs text-red-600" data-testid="vendor-seo-suggestion-error">{seoSuggestError}</p>
                ) : seoSuggestion ? (
                  <div className="space-y-2" data-testid="vendor-seo-suggestion-content-wrap">
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-stone-600" data-testid="vendor-seo-suggestion-word-count">{seoSuggestionWordCount} words suggested</p>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => setFormData((prev) => ({ ...prev, description: seoSuggestion }))}
                        className="bg-[#5C0120] hover:bg-[#5C0120]/90"
                        data-testid="vendor-seo-use-suggested-button"
                      >
                        Use Suggested
                      </Button>
                    </div>
                    <div className="max-h-48 overflow-auto rounded-sm border border-stone-200 bg-white p-3">
                      <p className="text-sm leading-relaxed text-stone-700 whitespace-pre-line" data-testid="vendor-seo-suggestion-text">{seoSuggestion}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-stone-500" data-testid="vendor-seo-suggestion-empty-state">
                    Start typing your description and we’ll suggest a stronger SEO-rich version below.
                  </p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-stone-600 mt-1 whitespace-pre-wrap break-words">{vendor?.description}</p>
          )}
        </div>
        
        {/* Contact Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-stone-500 text-xs uppercase tracking-wide flex items-center gap-1">
              <MapPin className="h-3 w-3" /> Address
            </Label>
            {editing ? (
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="mt-1"
              />
            ) : (
              <p className="text-stone-600 mt-1">{vendor?.address}</p>
            )}
          </div>
          <div>
            <Label className="text-stone-500 text-xs uppercase tracking-wide flex items-center gap-1">
              <Phone className="h-3 w-3" /> Phone
            </Label>
            {editing ? (
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1"
              />
            ) : (
              <p className="text-stone-600 mt-1">{vendor?.phone}</p>
            )}
          </div>
        </div>
        
        {/* Email - read only */}
        <div>
          <Label className="text-stone-500 text-xs uppercase tracking-wide flex items-center gap-1">
            <Mail className="h-3 w-3" /> Email
          </Label>
          <p className="text-stone-600 mt-1">{vendor?.email}</p>
        </div>

        <div>
          <Label className="text-stone-500 text-xs uppercase tracking-wide">Preferred Payment Method</Label>
          {editing ? (
            <Select value={formData.payment_method || 'Bank Transfer'} onValueChange={(value) => setFormData({ ...formData, payment_method: value })}>
              <SelectTrigger className="mt-1 w-64" data-testid="vendor-profile-payment-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                <SelectItem value="By Revolut">Revolut</SelectItem>
                <SelectItem value="By Card">Card</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <p className="text-stone-600 mt-1" data-testid="vendor-profile-payment-method-value">{vendor?.payment_method || '-'}</p>
          )}
        </div>
        
        {/* Social Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-stone-500 text-xs uppercase tracking-wide flex items-center gap-1">
              <Globe className="h-3 w-3" /> Website
            </Label>
            {editing ? (
              <Input
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://"
                className="mt-1"
              />
            ) : (
              <p className="text-stone-600 mt-1">{vendor?.website || '-'}</p>
            )}
          </div>
          <div>
            <Label className="text-stone-500 text-xs uppercase tracking-wide flex items-center gap-1">
              <Instagram className="h-3 w-3" /> Instagram
            </Label>
            {editing ? (
              <Input
                value={formData.instagram}
                onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                placeholder="@handle"
                className="mt-1"
              />
            ) : (
              <p className="text-stone-600 mt-1">{vendor?.instagram || '-'}</p>
            )}
          </div>
          <div>
            <Label className="text-stone-500 text-xs uppercase tracking-wide flex items-center gap-1">
              <Facebook className="h-3 w-3" /> Facebook
            </Label>
            {editing ? (
              <Input
                value={formData.facebook}
                onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                placeholder="Page name"
                className="mt-1"
              />
            ) : (
              <p className="text-stone-600 mt-1">{vendor?.facebook || '-'}</p>
            )}
          </div>
          <div>
            <Label className="text-stone-500 text-xs uppercase tracking-wide flex items-center gap-1">
              <MessageCircle className="h-3 w-3" /> WhatsApp
            </Label>
            {editing ? (
              <Input
                value={formData.whatsapp}
                onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                placeholder="+350 12345678"
                className="mt-1"
              />
            ) : (
              <p className="text-stone-600 mt-1">{vendor?.whatsapp || '-'}</p>
            )}
          </div>
        </div>
        
        {/* Price Range */}
        <div>
          <Label className="text-stone-500 text-xs uppercase tracking-wide">Price Range</Label>
          {editing ? (
            <div className="flex gap-2 mt-2">
              {['£', '££', '£££', '££££'].map((price) => (
                <button
                  key={price}
                  type="button"
                  onClick={() => setFormData({ ...formData, price_range: price })}
                  className={`px-4 py-2 rounded-sm border transition-colors ${
                    formData.price_range === price
                      ? 'bg-[#5C0120] border-[#5C0120] text-white'
                      : 'bg-white border-stone-200 text-stone-600 hover:border-[#5C0120]'
                  }`}
                >
                  {price}
                </button>
              ))}
            </div>
          ) : (
            <p className="font-medium mt-1 text-[#D4AF37]">{vendor?.price_range}</p>
          )}
        </div>

        <div className="rounded-[24px] border border-[#D4AF37]/25 bg-[#fffaf0] p-5 shadow-sm" data-testid="vendor-live-preview-box">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8f6b00]">Live Preview</p>
              <h3 className="text-lg font-semibold text-stone-900 mt-1">How your listing card will look</h3>
            </div>
            <p className="rounded-full bg-white px-3 py-1 text-xs font-medium text-[#5C0120] border border-[#5C0120]/15" data-testid="vendor-live-preview-save-note">
              Changes save automatically as you edit
            </p>
          </div>

          <div className="rounded-[22px] border border-stone-200 bg-white p-5 shadow-[0_16px_40px_rgba(28,25,23,0.06)]">
            <div className="flex flex-wrap gap-2 mb-4" data-testid="vendor-live-preview-categories">
              {formData.primaryCategory ? (
                <>
                  <span className="rounded-full bg-[#5C0120]/12 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-[#5C0120]" data-testid="vendor-live-preview-primary-category">
                    {formData.primaryCategory} (Primary)
                  </span>
                  {(formData.secondaryCategories || []).map((category) => (
                    <span key={category} className="rounded-full bg-[#D4AF37]/12 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-[#8f6b00]" data-testid={`vendor-live-preview-secondary-category-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      {category}
                    </span>
                  ))}
                </>
              ) : (
                <span className="rounded-full bg-stone-100 px-3 py-1 text-xs font-medium uppercase tracking-[0.18em] text-stone-500">No categories selected</span>
              )}
            </div>

            <h4 className="text-2xl font-semibold text-stone-900 mb-3" data-testid="vendor-live-preview-business-name">
              {formData.business_name || 'Your business name'}
            </h4>

            <p className="text-sm leading-relaxed text-stone-600 whitespace-pre-line break-words line-clamp-3 mb-4" data-testid="vendor-live-preview-description">
              {formData.description || 'Your description preview will appear here.'}
            </p>

            <div className="flex flex-wrap items-center gap-4 text-sm text-stone-500">
              <span data-testid="vendor-live-preview-address">{formData.address || 'Address'}</span>
              <span data-testid="vendor-live-preview-price-range">{formData.price_range || '££'}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MediaSection({ vendor, onUpdate }) {
  const [logoUrl, setLogoUrl] = useState('');
  const [imageUrls, setImageUrls] = useState([]);
  const [coverImage, setCoverImage] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Update state when vendor data changes
  useEffect(() => {
    if (vendor) {
      setLogoUrl(vendor.logo || '');
      setImageUrls(vendor.images || []);
      setCoverImage(vendor.cover_image || vendor.images?.[0] || '');
    }
  }, [vendor]);
  
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem('vendorToken');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_type', 'logo');
      
      const uploadRes = await axios.post(`${API}/vendor/upload?upload_type=logo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const newLogoUrl = uploadRes.data.url;
      setLogoUrl(newLogoUrl);
      
      // Save to profile
      await axios.put(`${API}/vendor/profile`, { logo: newLogoUrl }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onUpdate();
      alert('Logo uploaded successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };
  
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (imageUrls.length >= 10) {
      alert('Maximum 10 images allowed');
      return;
    }
    
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('vendorToken');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_type', 'image');
      
      const uploadRes = await axios.post(`${API}/vendor/upload?upload_type=image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const newImageUrl = uploadRes.data.url;
      const newImages = [...imageUrls, newImageUrl];
      setImageUrls(newImages);
      const nextCoverImage = coverImage || newImageUrl;
      setCoverImage(nextCoverImage);
      
      // Save to profile
      await axios.put(`${API}/vendor/profile`, { images: newImages, cover_image: nextCoverImage }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onUpdate();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      // Reset input
      e.target.value = '';
    }
  };
  
  const handleRemoveImage = async (index) => {
    const newImages = imageUrls.filter((_, i) => i !== index);
    const removedImage = imageUrls[index];
    const nextCoverImage = coverImage === removedImage ? (newImages[0] || '') : coverImage;
    setLoading(true);
    try {
      const token = localStorage.getItem('vendorToken');
      await axios.put(`${API}/vendor/profile`, { images: newImages, cover_image: nextCoverImage }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImageUrls(newImages);
      setCoverImage(nextCoverImage);
      onUpdate();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove image');
    } finally {
      setLoading(false);
    }
  };

  const handleSetCoverImage = async (imageUrl) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vendorToken');
      await axios.put(`${API}/vendor/profile`, { cover_image: imageUrl }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoverImage(imageUrl);
      onUpdate();
      alert('Cover photo updated.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update cover photo');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Logo Section */}
      <div className="bg-white rounded-sm border border-stone-200">
        <div className="p-6 border-b border-stone-100">
          <h2 className="text-lg font-semibold">Business Logo</h2>
          <p className="text-sm text-stone-500">Upload your business logo (max 10MB)</p>
        </div>
        <div className="p-6">
          <div className="flex items-start gap-6">
            <div className="w-32 h-32 bg-stone-100 rounded-sm flex items-center justify-center overflow-hidden">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <Store className="h-12 w-12 text-stone-300" />
              )}
            </div>
            <div className="flex-1 space-y-3">
              <div>
                <Label htmlFor="logo-upload" className="cursor-pointer">
                  <div className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white rounded-sm text-sm font-medium w-fit">
                    {uploadingLogo ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </div>
                </Label>
                <input
                  id="logo-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleLogoUpload}
                  className="hidden"
                  disabled={uploadingLogo}
                />
              </div>
              <p className="text-xs text-stone-500">Accepted formats: JPEG, PNG, WebP, GIF. Recommended size: 500x500px</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Gallery Section */}
      <div className="bg-white rounded-sm border border-stone-200">
        <div className="p-6 border-b border-stone-100">
          <h2 className="text-lg font-semibold">Image Gallery</h2>
          <p className="text-sm text-stone-500">Showcase your work with up to 10 images (max 10MB each)</p>
        </div>
        <div className="p-6">
          {coverImage && (
            <div className="mb-6 border border-stone-200 p-4 bg-stone-50">
              <p className="text-sm font-medium text-stone-700 mb-3">Current Cover Photo</p>
              <div className="aspect-[4/5] max-w-[220px] overflow-hidden bg-white border border-stone-200">
                <img src={coverImage} alt="Current cover" className="w-full h-full object-cover" />
              </div>
            </div>
          )}

          {/* Upload Button */}
          <div className="mb-6">
            <Label htmlFor="image-upload" className="cursor-pointer">
              <div className="flex items-center gap-2 px-4 py-2 bg-[#5C0120] hover:bg-[#5C0120]/90 text-white rounded-sm text-sm font-medium w-fit">
                {uploadingImage ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                {uploadingImage ? 'Uploading...' : 'Add Image'}
              </div>
            </Label>
            <input
              id="image-upload"
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleImageUpload}
              className="hidden"
              disabled={uploadingImage || imageUrls.length >= 10}
            />
            <p className="text-xs text-stone-500 mt-2">{imageUrls.length}/10 images uploaded</p>
          </div>
          
          {/* Image Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {imageUrls.map((url, index) => (
              <div key={index} className="relative group aspect-square bg-stone-100 rounded-sm overflow-hidden">
                <img src={url} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" />
                <button
                  onClick={() => handleSetCoverImage(url)}
                  className={`absolute bottom-2 left-2 px-2 py-1 text-[11px] uppercase tracking-[0.14em] font-medium ${coverImage === url ? 'bg-[#5C0120] text-white' : 'bg-white/90 text-[#5C0120]'}`}
                  disabled={loading}
                  data-testid={`vendor-set-cover-image-${index}`}
                >
                  {coverImage === url ? 'Cover Photo' : 'Set Cover'}
                </button>
                <button
                  onClick={() => handleRemoveImage(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  disabled={loading}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            {imageUrls.length === 0 && (
              <div className="col-span-full text-center py-8 text-stone-400">
                <Image className="h-12 w-12 mx-auto mb-2" />
                <p>No images yet. Click "Add Image" to upload.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function VideoSection({ vendor, onUpdate }) {
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  
  // Update state when vendor data changes
  useEffect(() => {
    if (vendor) {
      setVideoUrl(vendor.video_url || '');
    }
  }, [vendor]);
  
  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingVideo(true);
    try {
      const token = localStorage.getItem('vendorToken');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_type', 'video');
      
      const uploadRes = await axios.post(`${API}/vendor/upload?upload_type=video`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const newVideoUrl = uploadRes.data.url;
      setVideoUrl(newVideoUrl);
      
      // Save to profile
      await axios.put(`${API}/vendor/profile`, { video_url: newVideoUrl }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onUpdate();
      alert('Video uploaded successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploadingVideo(false);
      e.target.value = '';
    }
  };
  
  const handleRemoveVideo = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vendorToken');
      await axios.put(`${API}/vendor/profile`, { video_url: '' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideoUrl('');
      onUpdate();
      alert('Video removed');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove video');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="bg-white rounded-sm border border-stone-200">
      <div className="p-6 border-b border-stone-100">
        <h2 className="text-lg font-semibold">Promotional Video</h2>
        <p className="text-sm text-stone-500">Add a short video (max 50MB) to showcase your business</p>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {/* Upload Button */}
          <div>
            <Label htmlFor="video-upload" className="cursor-pointer">
              <div className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white rounded-sm text-sm font-medium w-fit">
                {uploadingVideo ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Video className="h-4 w-4" />
                )}
                {uploadingVideo ? 'Uploading...' : 'Upload Video'}
              </div>
            </Label>
            <input
              id="video-upload"
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              onChange={handleVideoUpload}
              className="hidden"
              disabled={uploadingVideo}
            />
            <p className="text-xs text-stone-500 mt-2">
              Accepted formats: MP4, WebM, MOV. Maximum size: 50MB
            </p>
          </div>
          
          {/* Video Preview */}
          {videoUrl && (
            <div className="space-y-3">
              <div className="aspect-video bg-black rounded-sm overflow-hidden">
                {videoUrl.includes('youtube') || videoUrl.includes('youtu.be') ? (
                  <iframe
                    src={videoUrl.replace('watch?v=', 'embed/')}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                    allowFullScreen
                  />
                ) : videoUrl.includes('vimeo') ? (
                  <iframe
                    src={`https://player.vimeo.com/video/${videoUrl.split('/').pop()}`}
                    className="w-full h-full"
                    allow="autoplay; fullscreen"
                    allowFullScreen
                  />
                ) : (
                  <video src={videoUrl} controls className="w-full h-full" />
                )}
              </div>
              <Button 
                onClick={handleRemoveVideo} 
                disabled={loading}
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
                Remove Video
              </Button>
            </div>
          )}
          
          {!videoUrl && (
            <div className="text-center py-8 text-stone-400 border-2 border-dashed border-stone-200 rounded-sm">
              <Video className="h-12 w-12 mx-auto mb-2" />
              <p>No video uploaded yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FeaturedSection({ vendor, onUpdate }) {
  const [loading, setLoading] = useState(false);
  
  const handleRequestFeatured = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vendorToken');
      await axios.post(`${API}/vendor/request-featured`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onUpdate();
      alert('Featured status requested! We will contact you with payment details.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to request featured status');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="bg-white rounded-sm border border-stone-200">
      <div className="p-6 border-b border-stone-100">
        <h2 className="text-lg font-semibold">Featured Vendor Status</h2>
        <p className="text-sm text-stone-500">Get more visibility by becoming a featured vendor</p>
      </div>
      <div className="p-6">
        {vendor?.is_featured ? (
          <div className="bg-[#D4AF37]/10 border border-[#D4AF37] rounded-sm p-6 text-center">
            <Star className="h-12 w-12 text-[#D4AF37] mx-auto mb-4 fill-current" />
            <h3 className="text-xl font-semibold mb-2">You're a Featured Vendor!</h3>
            <p className="text-stone-600">
              Your listing is highlighted across the directory and appears in the featured section on the homepage.
            </p>
          </div>
        ) : vendor?.featured_requested ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-6 text-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="h-6 w-6 text-yellow-600 animate-spin" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Request Pending</h3>
            <p className="text-stone-600">
              Your featured status request is being processed. We will contact you with payment details soon.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-stone-50 rounded-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Benefits of Featured Status</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>Highlighted listing with a "Featured" badge</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>Appear in the Featured Vendors section on the homepage</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>Priority placement in search results</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>Increased visibility and more inquiries</span>
                </li>
              </ul>
            </div>
            
            <div className="text-center">
              <p className="text-stone-600 mb-4">
                Interested in becoming a featured vendor? Request featured status and we'll contact you with pricing and payment details.
              </p>
              <Button 
                onClick={handleRequestFeatured} 
                disabled={loading}
                className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white px-8"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Star className="h-4 w-4 mr-2" />}
                Request Featured Status
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MarketingSection({ vendor, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [shareKit, setShareKit] = useState(null);
  const [formData, setFormData] = useState({
    special_offer_text: vendor?.special_offer_text || '',
    special_offer_code: vendor?.special_offer_code || '',
    special_offer_link: vendor?.special_offer_link || '',
    special_offer_expires_at: vendor?.special_offer_expires_at || '',
  });

  useEffect(() => {
    setFormData({
      special_offer_text: vendor?.special_offer_text || '',
      special_offer_code: vendor?.special_offer_code || '',
      special_offer_link: vendor?.special_offer_link || '',
      special_offer_expires_at: vendor?.special_offer_expires_at || '',
    });
  }, [vendor]);

  useEffect(() => {
    loadShareKit();
  }, []);

  const loadShareKit = async () => {
    try {
      const token = localStorage.getItem('vendorToken');
      const response = await axios.get(`${API}/vendor/share-kit`, { headers: { Authorization: `Bearer ${token}` } });
      setShareKit(response.data);
    } catch (err) {
      console.error('Failed to load share kit', err);
    }
  };

  const saveOffer = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vendorToken');
      await axios.put(`${API}/vendor/profile`, formData, { headers: { Authorization: `Bearer ${token}` } });
      onUpdate();
      alert('Marketing tools updated successfully.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save special offer');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (value, successText) => {
    try {
      await navigator.clipboard.writeText(value);
      alert(successText);
    } catch {
      alert('Unable to copy automatically. Please copy it manually.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-sm border border-stone-200">
        <div className="p-6 border-b border-stone-100">
          <h2 className="text-lg font-semibold">Special Offer Banner</h2>
          <p className="text-sm text-stone-500">Add a special offer that appears on your vendor card and listing page.</p>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <Label>Offer Headline</Label>
            <Input value={formData.special_offer_text} onChange={(e) => setFormData({ ...formData, special_offer_text: e.target.value })} className="mt-1" placeholder="e.g. 10% off weekday wedding bookings" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Offer Code / CTA</Label>
              <Input value={formData.special_offer_code} onChange={(e) => setFormData({ ...formData, special_offer_code: e.target.value })} className="mt-1" placeholder="e.g. GIBEXPO10" />
            </div>
            <div>
              <Label>Expiry Date</Label>
              <Input type="date" value={formData.special_offer_expires_at} onChange={(e) => setFormData({ ...formData, special_offer_expires_at: e.target.value })} className="mt-1" />
            </div>
          </div>
          <div>
            <Label>Offer Link</Label>
            <Input value={formData.special_offer_link} onChange={(e) => setFormData({ ...formData, special_offer_link: e.target.value })} className="mt-1" placeholder="https://yourwebsite.com/offer" />
          </div>
          <Button onClick={saveOffer} disabled={loading} className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white" data-testid="vendor-special-offer-save-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Megaphone className="h-4 w-4 mr-2" />}
            Save Offer
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-sm border border-stone-200">
        <div className="p-6 border-b border-stone-100">
          <h2 className="text-lg font-semibold">Listed on Gibraltar Wedding Expo Badge</h2>
          <p className="text-sm text-stone-500">Choose from 2 branded styles. Each option includes clickable embed code and a direct downloadable image link.</p>
        </div>
        <div className="p-6 space-y-6">
          <div className="space-y-4">
            <div>
              <Label>Your Listing Link</Label>
              <div className="mt-2 flex gap-2">
                <Input value={shareKit?.listing_url || ''} readOnly />
                <Button variant="outline" onClick={() => copyToClipboard(shareKit?.listing_url || '', 'Listing link copied!')}>
                  <Copy className="h-4 w-4 mr-2" /> Copy
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {(shareKit?.styles || []).map((style) => (
              <div key={style.key} className="rounded-[24px] border border-stone-200 bg-stone-50 p-5 space-y-4" data-testid={`vendor-share-style-${style.key}`}>
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-stone-500">{style.name}</p>
                  <p className="text-sm text-stone-600 mt-1">{style.description}</p>
                </div>
                <div className="border border-stone-200 bg-white p-4 flex items-center justify-center min-h-[160px] overflow-hidden">
                  <img src={style.image_url} alt={style.name} className="w-auto h-auto" style={{ width: `${style.preview_width}px`, maxWidth: '100%' }} />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => window.open(style.download_url, '_blank')}
                    className="border-[#5C0120] text-[#5C0120] hover:bg-[#5C0120]/10"
                  >
                    <Download className="h-4 w-4 mr-2" /> Download Image
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => copyToClipboard(style.image_url, 'Image URL copied!')}
                    className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  >
                    <Copy className="h-4 w-4 mr-2" /> Copy Image URL
                  </Button>
                </div>
                <div>
                  <Label>Clickable Website Code</Label>
                  <textarea
                    className="mt-2 min-h-[190px] w-full rounded-sm border border-stone-200 p-3 text-sm text-stone-700"
                    readOnly
                    value={style.embed_code}
                    data-testid={`vendor-share-kit-embed-code-${style.key}`}
                  />
                  <div className="mt-2 flex gap-2">
                    <Button 
                      onClick={() => copyToClipboard(style.embed_code, `${style.name} embed code copied!`)}
                      className="bg-[#5C0120] hover:bg-[#5C0120]/90 text-white"
                    >
                      <BadgeCheck className="h-4 w-4 mr-2" /> Copy Embed Code
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

function VendorAdminUpdatesSection({ onUnreadChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyDrafts, setReplyDrafts] = useState({});

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const token = localStorage.getItem('vendorToken');
      const response = await axios.get(`${API}/vendor/admin-messages`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(response.data);
      onUnreadChange?.((response.data || []).filter((thread) => !thread.read || thread.status === 'unread').length);
    } catch (err) {
      console.error('Failed to load admin messages', err);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    const token = localStorage.getItem('vendorToken');
    await axios.put(`${API}/vendor/admin-messages/${messageId}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
    loadMessages();
  };

  const sendReply = async (messageId) => {
    const message = replyDrafts[messageId]?.trim();
    if (!message) return;
    try {
      const token = localStorage.getItem('vendorToken');
      await axios.post(`${API}/vendor/admin-messages/${messageId}/reply`, { message }, { headers: { Authorization: `Bearer ${token}` } });
      setReplyDrafts((prev) => ({ ...prev, [messageId]: '' }));
      loadMessages();
      alert('Reply sent to admin.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send reply');
    }
  };

  return (
    <div className="bg-white rounded-sm border border-stone-200">
      <div className="p-6 border-b border-stone-100">
        <h2 className="text-lg font-semibold">Admin Updates</h2>
        <p className="text-sm text-stone-500">Important messages sent to you by the Gibraltar Wedding Expo admin team.</p>
      </div>
      <div className="p-6 space-y-4">
        {loading ? (
          <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin text-stone-400 mx-auto" /></div>
        ) : messages.length === 0 ? (
          <div className="rounded-sm border border-dashed border-stone-200 p-8 text-center text-stone-500">No admin messages yet.</div>
        ) : messages.map((message) => (
          <div key={message.id} className="rounded-sm border border-stone-200 p-5" data-testid={`vendor-admin-message-${message.id}`}>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold">{message.subject}</h3>
                  {!message.read && <span className="rounded-full bg-[#D4AF37]/15 px-3 py-1 text-xs text-[#5C0120]">New</span>}
                </div>
                <p className="text-xs text-stone-500 mt-1">Sent {new Date(message.created_at).toLocaleString('en-GB')}</p>
              </div>
              {!message.read && <Button variant="outline" size="sm" onClick={() => markAsRead(message.id)}>Mark Read</Button>}
            </div>
            <div className="space-y-3 mt-4">
              {(message.messages || []).map((entry) => (
                <div key={entry.id} className={`max-w-[85%] rounded-sm border p-3 ${entry.sender_role === 'vendor' ? 'ml-auto bg-[#5C0120] text-white border-[#5C0120]' : 'bg-stone-50 border-stone-200 text-stone-800'}`}>
                  <p className={`text-[11px] uppercase tracking-[0.14em] mb-2 ${entry.sender_role === 'vendor' ? 'text-white/70' : 'text-stone-500'}`}>{entry.sender_name}</p>
                  <p className="text-sm whitespace-pre-wrap">{entry.message}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-3">
              <Textarea
                rows={4}
                value={replyDrafts[message.id] || ''}
                onChange={(e) => setReplyDrafts((prev) => ({ ...prev, [message.id]: e.target.value }))}
                placeholder="Reply to the admin team..."
                data-testid={`vendor-admin-reply-input-${message.id}`}
              />
              <div className="flex justify-end">
                <Button onClick={() => sendReply(message.id)} className="bg-[#5C0120] hover:bg-[#5C0120]/90 text-white" data-testid={`vendor-admin-reply-button-${message.id}`}>
                  Reply to Admin
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Vendor Messages Section - Vendor Inbox
function VendorMessagesSection({ onUnreadChange }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadConversations();
    loadUnreadCount();
  }, []);

  const getToken = () => localStorage.getItem('vendorToken');

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/vendor/messages`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setConversations(response.data);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/vendor/messages/unread/count`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setUnreadCount(response.data.unread_count);
      onUnreadChange?.(response.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to load unread count:', err);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/vendor/messages/${conversationId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSelectedConversation(response.data);
      loadUnreadCount(); // Refresh unread count
    } catch (err) {
      console.error('Failed to load conversation:', err);
    }
  };

  const deleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;
    try {
      await axios.delete(`${API}/vendor/messages/${conversationId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setSelectedConversation(null);
      loadConversations(); // Refresh list
      loadUnreadCount();
    } catch (err) {
      console.error('Failed to delete conversation:', err);
      alert('Failed to delete conversation');
    }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/vendor/messages/${selectedConversation.conversation_id}/reply`,
        { message: replyText },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setSelectedConversation(response.data);
      setReplyText('');
      loadConversations(); // Refresh list
    } catch (err) {
      console.error('Failed to send reply:', err);
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'replied':
        return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">Replied</span>;
      case 'read':
        return <span className="px-2 py-1 text-xs bg-[#D4AF37]/15 text-[#5C0120] rounded-full">Read</span>;
      default:
        return <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">New</span>;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-sm border border-stone-200 p-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37] mx-auto" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-sm border border-stone-200">
      <div className="p-6 border-b border-stone-200">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Inbox className="h-5 w-5 text-[#D4AF37]" />
              Customer Inquiries
            </h2>
            <p className="text-sm text-stone-500 mt-1">
              Messages from couples interested in your services
            </p>
          </div>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-sm px-3 py-1 rounded-full">
              {unreadCount} new
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 min-h-[500px]">
        {/* Conversations List */}
        <div className="border-r border-stone-200">
          {conversations.length === 0 ? (
            <div className="p-8 text-center text-stone-500">
              <Inbox className="h-16 w-16 mx-auto mb-4 text-stone-300" />
              <p className="font-medium">No messages yet</p>
              <p className="text-sm mt-2">
                When couples contact you through your listing, their messages will appear here.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-stone-100 max-h-[500px] overflow-y-auto">
              {conversations.map((conv) => (
                <button
                  key={conv.conversation_id}
                  onClick={() => loadConversation(conv.conversation_id)}
                  className={`w-full p-4 text-left hover:bg-stone-50 transition-colors ${
                    selectedConversation?.conversation_id === conv.conversation_id 
                      ? 'bg-[#D4AF37]/5 border-l-4 border-[#D4AF37]' 
                      : conv.status === 'unread' ? 'bg-red-50/50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-sm">{conv.user_name}</p>
                        <span className="rounded-full bg-[#5C0120]/10 px-2 py-0.5 text-[11px] font-medium text-[#5C0120]" data-testid={`vendor-lead-source-badge-${conv.conversation_id}`}>
                          Expo lead
                        </span>
                      </div>
                      <p className="text-xs text-stone-500 truncate">{conv.subject}</p>
                    </div>
                    {getStatusBadge(conv.status)}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-stone-400">{formatDate(conv.updated_at || conv.created_at)}</p>
                    {conv.wedding_date && (
                      <p className="text-xs text-stone-500 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {conv.wedding_date}
                      </p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Conversation Detail */}
        <div className="lg:col-span-2 flex flex-col">
          {selectedConversation ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-stone-200 bg-stone-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">{selectedConversation.user_name}</h4>
                    <p className="text-sm text-stone-500">{selectedConversation.user_email}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-[#5C0120]/10 px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.16em] text-[#5C0120]" data-testid="vendor-conversation-lead-source-badge">
                        Lead source: Gibraltar Wedding Expo
                      </span>
                      <p className="text-xs text-stone-500" data-testid="vendor-conversation-lead-source-note">
                        This couple found you through Gibraltar Wedding Expo.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium">{selectedConversation.subject}</p>
                      {selectedConversation.wedding_date && (
                        <p className="text-xs text-stone-500">
                          Wedding: {selectedConversation.wedding_date}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteConversation(selectedConversation.conversation_id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-sm transition-colors"
                      title="Delete conversation"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[350px]">
                {selectedConversation.messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.sender === 'vendor' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-lg p-3 ${
                        msg.sender === 'vendor'
                          ? 'bg-[#D4AF37] text-white'
                          : 'bg-stone-100 text-stone-800'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className={`text-xs mt-2 ${msg.sender === 'vendor' ? 'text-white/70' : 'text-stone-400'}`}>
                        {msg.sender_name} • {formatDate(msg.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Reply Input */}
              <div className="p-4 border-t border-stone-200 bg-stone-50">
                <div className="flex gap-2">
                  <Input
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply to the customer..."
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendReply()}
                    className="flex-1"
                  />
                  <Button 
                    onClick={sendReply} 
                    disabled={!replyText.trim() || sending}
                    className="bg-[#D4AF37] hover:bg-[#D4AF37]/90"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-stone-400">
              <div className="text-center">
                <MessageCircle className="h-16 w-16 mx-auto mb-4" />
                <p className="font-medium">Select a message to view</p>
                <p className="text-sm mt-1">Click on a conversation to read and reply</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// Vendor Account Section - Password Change
function VendorAccountSection({ forcePasswordChangeRequired = false, onPasswordChanged }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('vendorToken');
      await axios.post(`${API}/vendor/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: 'Password changed successfully!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      if (onPasswordChanged) {
        await onPasswordChanged();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to change password' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-sm border border-stone-200">
      <div className="p-6 border-b border-stone-100">
        <div className="flex items-center gap-3">
          <Lock className="h-5 w-5 text-[#5C0120]" />
          <div>
            <h2 className="text-lg font-semibold">Account Settings</h2>
            <p className="text-sm text-stone-500">Manage your password and security</p>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-md">
        {forcePasswordChangeRequired && (
          <div className="mb-4 rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="vendor-account-force-password-change-note">
            You are using a temporary password. Please set a new one now.
          </div>
        )}
        <h3 className="font-medium mb-4">Change Password</h3>
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <Label htmlFor="vendor-current-password">Current Password</Label>
            <Input
              id="vendor-current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="mt-1"
              data-testid="vendor-current-password-input"
            />
          </div>

          <div>
            <Label htmlFor="vendor-new-password">New Password</Label>
            <Input
              id="vendor-new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              className="mt-1"
              data-testid="vendor-new-password-input"
            />
          </div>

          <div>
            <Label htmlFor="vendor-confirm-password">Confirm New Password</Label>
            <Input
              id="vendor-confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="mt-1"
              data-testid="vendor-confirm-password-input"
            />
          </div>

          {message.text && (
            <div className={`p-3 rounded-sm text-sm ${
              message.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {message.text}
            </div>
          )}

          <Button 
            type="submit" 
            disabled={loading}
            className="bg-[#D4AF37] hover:bg-[#D4AF37]/90"
            data-testid="vendor-change-password-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Change Password
          </Button>
        </form>
      </div>
    </div>
  );
}

function VendorInvoicesSection() {
  const [invoices, setInvoices] = useState([]);
  const [billingSummary, setBillingSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadInvoices = async () => {
      try {
        const token = localStorage.getItem('vendorToken');
        const response = await axios.get(`${API}/vendor/invoices`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInvoices(response.data || []);

        const vendorProfileResponse = await axios.get(`${API}/vendor/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBillingSummary(vendorProfileResponse.data || null);
      } catch (err) {
        console.error('Failed to load invoices', err);
      } finally {
        setLoading(false);
      }
    };

    loadInvoices();
  }, []);

  return (
    <div className="bg-white rounded-sm border border-stone-200 p-8">
      <div className="mb-6">
        <h2 className="heading-lg mb-2">Invoices</h2>
        <p className="text-stone-600">Download invoices generated by the Gibraltar Wedding Expo admin team.</p>
      </div>

      <div className="mb-6 rounded-sm border border-stone-200 bg-stone-50 p-4" data-testid="vendor-billing-summary-card">
        <p className="text-xs uppercase tracking-wide text-stone-500">Current Billing</p>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-stone-700">
          <span data-testid="vendor-billing-summary-plan"><strong>Package:</strong> {billingSummary?.pricing_plan || 'Not set'}</span>
          <span data-testid="vendor-billing-summary-next-payment"><strong>Next payment:</strong> {formatPaymentDate(billingSummary?.next_payment_date)}</span>
        </div>
      </div>

      {loading ? (
        <div className="text-stone-500">Loading invoices...</div>
      ) : invoices.length === 0 ? (
        <div className="rounded-sm border border-stone-200 bg-stone-50 p-5 text-stone-600" data-testid="vendor-invoices-empty-state">
          No invoices have been generated yet.
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map((invoice) => (
            <div key={invoice.id} className="flex flex-wrap items-center justify-between gap-4 rounded-sm border border-stone-200 p-4" data-testid={`vendor-invoice-row-${invoice.id}`}>
              <div>
                <p className="font-medium text-stone-900">Invoice {invoice.invoice_number}</p>
                <p className="text-sm text-stone-500">{invoice.pricing_plan} • {invoice.payment_method || 'Payment method to be confirmed'}</p>
                <p className="text-sm text-stone-500">Issued {new Date(invoice.invoice_date).toLocaleDateString()}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-semibold text-stone-900">£{invoice.amount}</span>
                <Button onClick={() => downloadVendorInvoicePdf(invoice)} data-testid={`vendor-download-invoice-${invoice.id}`}>
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
